document.documentElement.classList.remove('no-js');

function initHeader(header) {
  if (!header || header.dataset.vsReady === 'true') return;
  header.dataset.vsReady = 'true';

  const menu = header.querySelector('[data-mobile-menu]');
  const openButton = header.querySelector('[data-menu-toggle]');
  const closeButton = header.querySelector('[data-menu-close]');
  let lastFocusedElement = null;

  function openMenu() {
    if (!menu || !openButton) return;
    lastFocusedElement = document.activeElement;
    menu.hidden = false;
    document.body.classList.add('menu-open');
    openButton.setAttribute('aria-expanded', 'true');
    closeButton?.focus();
  }

  function closeMenu(restoreFocus = true) {
    if (!menu || !openButton) return;
    menu.hidden = true;
    document.body.classList.remove('menu-open');
    openButton.setAttribute('aria-expanded', 'false');
    if (restoreFocus && lastFocusedElement instanceof HTMLElement) {
      lastFocusedElement.focus();
    }
  }

  openButton?.addEventListener('click', openMenu);
  closeButton?.addEventListener('click', () => closeMenu());

  menu?.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => closeMenu(false));
  });

  header.querySelectorAll('[data-mobile-submenu-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const submenuId = button.getAttribute('aria-controls');
      const submenu = submenuId ? document.getElementById(submenuId) : null;
      if (!submenu) return;
      const isOpen = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!isOpen));
      submenu.hidden = isOpen;
    });
  });

  const desktopItems = Array.from(header.querySelectorAll('[data-desktop-nav-item]'));

  function closeDesktopSubmenus(exceptItem = null) {
    desktopItems.forEach((item) => {
      if (item === exceptItem) return;
      item.classList.remove('is-open');
      item.querySelector('[data-desktop-submenu-toggle]')?.setAttribute('aria-expanded', 'false');
    });
  }

  header.querySelectorAll('[data-desktop-submenu-toggle]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const item = button.closest('[data-desktop-nav-item]');
      if (!item) return;
      const isOpen = item.classList.contains('is-open');
      closeDesktopSubmenus(item);
      item.classList.toggle('is-open', !isOpen);
      button.setAttribute('aria-expanded', String(!isOpen));
    });
  });

  document.addEventListener('click', (event) => {
    if (!header.contains(event.target)) closeDesktopSubmenus();
  });

  header.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (menu && !menu.hidden) {
      closeMenu();
      return;
    }
    closeDesktopSubmenus();
    openButton?.focus();
  });
}

function initQuickAddForms(root = document) {
  root.querySelectorAll('.vs-quick-form:not([data-vs-ready])').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    form.dataset.vsReady = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('button');
      if (!button) return;

      const original = button.innerHTML;
      button.disabled = true;
      button.textContent = 'Añadiendo…';

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: new FormData(form),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(payload?.description || payload?.message || 'No se pudo añadir');
        }
        button.textContent = 'Añadido ✓';
        setTimeout(() => window.location.assign(window.Shopify.routes.root + 'cart'), 450);
      } catch (error) {
        console.error('[vs] cart/add:', error);
        button.textContent = 'No disponible';
        form.querySelector('[data-quick-error]')?.remove();
        const notice = document.createElement('p');
        notice.className = 'vs-quick-error';
        notice.setAttribute('data-quick-error', '');
        notice.setAttribute('role', 'alert');
        notice.textContent = error.message;
        form.append(notice);
        setTimeout(() => notice.remove(), 5000);
      } finally {
        setTimeout(() => {
          button.disabled = false;
          button.innerHTML = original;
        }, 1500);
      }
    });
  });
}

function initEditorialCarousel(carousel) {
  if (!carousel || carousel.dataset.vsReady === 'true') return;
  carousel.dataset.vsReady = 'true';

  const track = carousel.querySelector('[data-carousel-track]');
  const slides = Array.from(carousel.querySelectorAll('[data-carousel-slide]'));
  const dots = Array.from(carousel.querySelectorAll('[data-carousel-dot]'));
  const currentLabel = carousel.querySelector('[data-carousel-current]');
  const previousButton = carousel.querySelector('[data-carousel-prev]');
  const nextButton = carousel.querySelector('[data-carousel-next]');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const autoplay = carousel.dataset.autoplay === 'true' && !prefersReducedMotion;
  const interval = Math.max(Number(carousel.dataset.interval) || 6000, 3000);
  let currentIndex = 0;
  let autoplayTimer = null;
  let scrollTimer = null;

  if (!track || slides.length === 0) return;

  function updateControls(index) {
    currentIndex = (index + slides.length) % slides.length;
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === currentIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (currentLabel) currentLabel.textContent = String(currentIndex + 1).padStart(2, '0');
  }

  function goTo(index, smooth = true) {
    const nextIndex = (index + slides.length) % slides.length;
    const slide = slides[nextIndex];
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft,
      behavior: smooth && !prefersReducedMotion ? 'smooth' : 'auto',
    });
    updateControls(nextIndex);
  }

  function stopAutoplay() {
    if (autoplayTimer) window.clearInterval(autoplayTimer);
    autoplayTimer = null;
  }

  function startAutoplay() {
    stopAutoplay();
    if (!autoplay || slides.length < 2) return;
    autoplayTimer = window.setInterval(() => goTo(currentIndex + 1), interval);
  }

  previousButton?.addEventListener('click', () => {
    goTo(currentIndex - 1);
    startAutoplay();
  });
  nextButton?.addEventListener('click', () => {
    goTo(currentIndex + 1);
    startAutoplay();
  });
  dots.forEach((dot, index) => {
    dot.addEventListener('click', () => {
      goTo(index);
      startAutoplay();
    });
  });

  track.addEventListener('scroll', () => {
    window.clearTimeout(scrollTimer);
    scrollTimer = window.setTimeout(() => {
      const trackLeft = track.getBoundingClientRect().left;
      let closestIndex = 0;
      let closestDistance = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      updateControls(closestIndex);
    }, 90);
  }, { passive: true });

  carousel.addEventListener('mouseenter', stopAutoplay);
  carousel.addEventListener('mouseleave', startAutoplay);
  carousel.addEventListener('focusin', stopAutoplay);
  carousel.addEventListener('focusout', (event) => {
    if (!carousel.contains(event.relatedTarget)) startAutoplay();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopAutoplay();
    else startAutoplay();
  });

  if (carousel.dataset.dailyStart === 'true') {
    const dayNumber = Math.floor(Date.now() / 86400000);
    currentIndex = dayNumber % slides.length;
  }

  window.requestAnimationFrame(() => goTo(currentIndex, false));
  startAutoplay();
}

function initPolicyDialogs(root = document) {
  root.querySelectorAll('[data-policy-open]:not([data-vs-ready])').forEach((button) => {
    button.dataset.vsReady = 'true';
    button.addEventListener('click', () => {
      const dialogId = button.getAttribute('data-policy-open');
      const dialog = dialogId ? document.getElementById(dialogId) : null;
      if (!(dialog instanceof HTMLDialogElement)) return;
      dialog.showModal();
      dialog.querySelector('[data-policy-close]')?.focus();
    });
  });

  root.querySelectorAll('.vs-policy-dialog:not([data-vs-ready])').forEach((dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return;
    dialog.dataset.vsReady = 'true';
    dialog.querySelector('[data-policy-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  });
}

function initVariantMedia(root = document) {
  const productSections = root.matches?.('[data-product-section]')
    ? [root]
    : Array.from(root.querySelectorAll('[data-product-section]'));

  productSections.forEach((section) => {
    if (section.dataset.vsProductReady === 'true') return;

    const gallery = section.querySelector('[data-variant-media-gallery]');
    const variantDataElement = section.querySelector('[data-product-variants]');
    const variantSelect = section.querySelector('[data-variant-select]');
    const hiddenVariantInput = section.querySelector('input[name="id"]');
    const price = section.querySelector('[data-variant-price]');
    const addToCartButton = section.querySelector('.vs-product-form button[type="submit"]');
    const addToCartLabel = addToCartButton?.querySelector('[data-add-to-cart-label]');
    const galleryStatus = section.querySelector('[data-gallery-status]');
    const mediaItems = gallery ? Array.from(gallery.querySelectorAll('[data-product-media]')) : [];
    const filterVariantMedia = section.dataset.filterVariantMedia === 'true';

    if (!gallery || !variantDataElement || mediaItems.length === 0) return;

    let variantPayload;
    try {
      variantPayload = JSON.parse(variantDataElement.textContent);
    } catch {
      return;
    }

    const variants = new Map(
      (variantPayload.variants || []).map((variant) => [String(variant.id), variant]),
    );
    let statusTimer = null;

    function getVisibleMedia() {
      return mediaItems.filter((item) => !item.hidden);
    }

    function updateGalleryStatus() {
      if (!galleryStatus) return;
      const visibleMedia = getVisibleMedia();
      galleryStatus.hidden = visibleMedia.length < 2;
      if (visibleMedia.length < 2) return;

      const galleryLeft = gallery.getBoundingClientRect().left;
      let currentIndex = 0;
      let closestDistance = Infinity;
      visibleMedia.forEach((item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - galleryLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          currentIndex = index;
        }
      });
      galleryStatus.textContent = (currentIndex + 1) + ' / ' + visibleMedia.length;
    }

    function showVariantMedia(mediaGroup) {
      const activeGroup = mediaGroup == null ? null : String(mediaGroup);
      mediaItems.forEach((item) => {
        const itemGroup = item.dataset.mediaGroup;
        item.hidden = Boolean(
          filterVariantMedia
          && activeGroup
          && itemGroup !== 'shared'
          && itemGroup !== activeGroup,
        );
      });

      gallery.scrollTo({ left: 0, behavior: 'auto' });
      window.requestAnimationFrame(updateGalleryStatus);
    }

    function updatePrice(variant) {
      if (!price) return;
      const currentPrice = price.querySelector('strong');
      let compareAtPrice = price.querySelector('s');
      if (currentPrice) currentPrice.textContent = variant.price;

      if (variant.compareAtPrice) {
        if (!compareAtPrice) {
          compareAtPrice = document.createElement('s');
          price.append(compareAtPrice);
        }
        compareAtPrice.textContent = variant.compareAtPrice;
      } else {
        compareAtPrice?.remove();
      }
    }

    function updateVariant(variantId, updateUrl = true) {
      const variant = variants.get(String(variantId));
      if (!variant) return;

      showVariantMedia(variant.mediaGroup);
      updatePrice(variant);

      if (addToCartButton) addToCartButton.disabled = !variant.available;
      if (addToCartLabel) addToCartLabel.textContent = variant.available ? 'Añadir a la cesta' : 'Agotado';

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      section.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant },
      }));
    }

    variantSelect?.addEventListener('change', () => updateVariant(variantSelect.value));
    gallery.addEventListener('scroll', () => {
      window.clearTimeout(statusTimer);
      statusTimer = window.setTimeout(updateGalleryStatus, 80);
    }, { passive: true });

    section.dataset.vsProductReady = 'true';
    updateVariant(variantSelect?.value || hiddenVariantInput?.value, false);
  });
}


function initVariantPicker(root = document) {
  const sections = root.matches?.('[data-product-section]')
    ? [root]
    : Array.from(root.querySelectorAll('[data-product-section]'));

  sections.forEach((section) => {
    const groups = section.querySelector('[data-option-groups]');
    const select = section.querySelector('[data-variant-select]');
    const selectLabel = section.querySelector('[data-variant-select-label]');
    const dataElement = section.querySelector('[data-product-variants]');
    if (!groups || !select || !dataElement || groups.dataset.vsReady === 'true') return;
    groups.dataset.vsReady = 'true';

    let payload;
    try {
      payload = JSON.parse(dataElement.textContent);
    } catch {
      return;
    }

    const variants = (payload.variants || []).filter((variant) => Array.isArray(variant.options));
    // Si por lo que sea no hay datos de opciones, dejamos el desplegable a la vista.
    if (variants.length === 0) {
      groups.remove();
      return;
    }

    // Los botones sustituyen al desplegable, que se queda para navegadores sin JS.
    groups.hidden = false;
    select.hidden = true;
    if (selectLabel) selectLabel.hidden = true;

    const buttons = Array.from(groups.querySelectorAll('[data-option-button]'));

    function currentSelection() {
      const selection = [];
      groups.querySelectorAll('[data-option-group]').forEach((group) => {
        const position = Number(group.dataset.optionGroup);
        if (!position) return;
        const active = group.querySelector('[data-option-button].is-active');
        selection[position - 1] = active ? active.dataset.optionValue : null;
      });
      return selection;
    }

    function findVariant(selection) {
      return (
        variants.find(
          (variant) =>
            variant.available && selection.every((value, index) => value == null || variant.options[index] === value),
        ) ||
        variants.find((variant) => selection.every((value, index) => value == null || variant.options[index] === value))
      );
    }

    function refreshAvailability() {
      const selection = currentSelection();
      buttons.forEach((button) => {
        const position = Number(button.dataset.optionPosition);
        const probe = selection.slice();
        probe[position - 1] = button.dataset.optionValue;
        const match = variants.some(
          (variant) =>
            variant.available && probe.every((value, index) => value == null || variant.options[index] === value),
        );
        button.classList.toggle('is-unavailable', !match);
      });

      groups.querySelectorAll('[data-option-group]').forEach((group) => {
        const active = group.querySelector('[data-option-button].is-active');
        const label = group.querySelector('[data-option-selected]');
        if (label) label.textContent = active ? active.dataset.optionValue : '';
      });
    }

    function syncFromSelect() {
      const variant = variants.find((item) => String(item.id) === String(select.value));
      if (!variant) return;
      buttons.forEach((button) => {
        const position = Number(button.dataset.optionPosition);
        const isActive = variant.options[position - 1] === button.dataset.optionValue;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
      refreshAvailability();
    }

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const position = Number(button.dataset.optionPosition);
        const group = button.closest('[data-option-group]');
        group?.querySelectorAll('[data-option-button]').forEach((sibling) => {
          const isActive = sibling === button;
          sibling.classList.toggle('is-active', isActive);
          sibling.setAttribute('aria-pressed', String(isActive));
        });

        const selection = currentSelection();
        let variant = findVariant(selection);

        if (!variant) {
          // No existe esa combinación: mantenemos la opción tocada y soltamos las demás.
          const relaxed = selection.map((value, index) => (index === position - 1 ? value : null));
          variant = findVariant(relaxed);
        }
        if (!variant) return;

        select.value = variant.id;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        syncFromSelect();
      });
    });

    select.addEventListener('change', syncFromSelect);
    syncFromSelect();
  });
}

function initCardSwatches(root = document) {
  const cards = Array.from(root.querySelectorAll('[data-product-card]:not([data-vs-swatches-ready])'));

  cards.forEach((card) => {
    const swatches = Array.from(card.querySelectorAll('[data-card-swatch]'));
    if (swatches.length === 0) return;
    card.dataset.vsSwatchesReady = 'true';

    const image = card.querySelector('[data-card-image]');
    const links = Array.from(card.querySelectorAll('[data-card-link]'));
    const originalSrc = image?.getAttribute('src');
    const originalSrcset = image?.getAttribute('srcset');
    const originalHrefs = links.map((link) => link.getAttribute('href'));

    function applySwatch(swatch) {
      swatches.forEach((item) => item.classList.toggle('is-active', item === swatch));
      if (image && swatch.dataset.image) {
        image.setAttribute('src', swatch.dataset.image);
        image.removeAttribute('srcset');
      }
      if (swatch.dataset.url) {
        links.forEach((link) => link.setAttribute('href', swatch.dataset.url));
      }
    }

    function resetSwatch() {
      if (image && originalSrc) {
        image.setAttribute('src', originalSrc);
        if (originalSrcset) image.setAttribute('srcset', originalSrcset);
      }
      links.forEach((link, index) => link.setAttribute('href', originalHrefs[index]));
      swatches.forEach((item, index) => item.classList.toggle('is-active', index === 0));
    }

    swatches.forEach((swatch) => {
      swatch.addEventListener('mouseenter', () => applySwatch(swatch));
      swatch.addEventListener('focus', () => applySwatch(swatch));
      swatch.addEventListener('click', () => {
        applySwatch(swatch);
        if (swatch.dataset.url) window.location.assign(swatch.dataset.url);
      });
    });

    card.addEventListener('mouseleave', resetSwatch);
    card.dataset.vsResetSwatch = 'true';
    card.vsResetSwatch = resetSwatch;
    card.vsApplySwatch = applySwatch;
  });
}

function initCollectionFilters(root = document) {
  const bars = Array.from(root.querySelectorAll('[data-collection-filters]:not([data-vs-ready])'));

  bars.forEach((bar) => {
    bar.dataset.vsReady = 'true';

    const section = bar.closest('section') || document;
    const cards = Array.from(section.querySelectorAll('[data-product-card]'));
    const emptyState = section.querySelector('[data-filter-empty]');
    const count = section.querySelector('[data-collection-count]');
    const countTemplate = count?.textContent || '';
    const clearButtons = Array.from(section.querySelectorAll('[data-filter-clear]'));
    const dropdown = bar.closest('[data-filter-dropdown]');
    const toggle = dropdown?.querySelector('[data-filter-toggle]');
    const toggleCount = dropdown?.querySelector('[data-filter-toggle-count]');
    if (cards.length === 0) return;

    function closeDropdown() {
      if (!toggle) return;
      bar.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }

    function openDropdown() {
      if (!toggle) return;
      bar.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    }

    if (toggle) {
      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        if (bar.hidden) openDropdown();
        else closeDropdown();
      });

      document.addEventListener('click', (event) => {
        if (bar.hidden) return;
        if (dropdown.contains(event.target)) return;
        closeDropdown();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !bar.hidden) {
          closeDropdown();
          toggle.focus();
        }
      });
    }

    function activeValues(groupName) {
      return Array.from(
        bar.querySelectorAll('[data-filter-group="' + groupName + '"] .vs-filter-chip.is-active'),
      ).map((chip) => chip.dataset.filterValue);
    }

    function apply() {
      const colors = activeValues('color');
      const sizes = activeValues('size');
      const priceBuckets = activeValues('price').map((value) => {
        const [min, max] = value.split('-').map(Number);
        return { min, max };
      });
      const onlyAvailable = activeValues('availability').includes('true');
      let visible = 0;

      cards.forEach((card) => {
        const cardFamilies = card.dataset.families || '';
        const cardSizes = card.dataset.sizes || '';
        const cardPrice = Number(card.dataset.price || 0);
        const cardAvailable = card.dataset.available === 'true';
        const matchesColor = colors.length === 0 || colors.some((value) => cardFamilies.includes('|' + value + '|'));
        const matchesSize = sizes.length === 0 || sizes.some((value) => cardSizes.includes('|' + value + '|'));
        const matchesPrice =
          priceBuckets.length === 0 ||
          priceBuckets.some((bucket) => cardPrice >= bucket.min && cardPrice < bucket.max);
        const matchesAvailability = !onlyAvailable || cardAvailable;
        const matches = matchesColor && matchesSize && matchesPrice && matchesAvailability;
        card.hidden = !matches;
        if (matches) {
          visible += 1;
          if (colors.length === 1 && card.vsApplySwatch) {
            const swatch = card.querySelector('[data-card-swatch][data-family="' + colors[0] + '"]');
            if (swatch) card.vsApplySwatch(swatch);
          } else if (card.vsResetSwatch) {
            card.vsResetSwatch();
          }
        }
      });

      const activeCount =
        colors.length + sizes.length + priceBuckets.length + (onlyAvailable ? 1 : 0);
      const hasFilters = activeCount > 0;
      clearButtons.forEach((button) => {
        button.hidden = !hasFilters;
      });
      if (emptyState) emptyState.hidden = visible !== 0;
      if (count) {
        count.textContent = hasFilters
          ? visible + (visible === 1 ? ' producto' : ' productos') + ' con estos filtros'
          : countTemplate;
      }
      if (toggleCount) {
        toggleCount.hidden = !hasFilters;
        toggleCount.textContent = String(activeCount);
      }
    }

    bar.querySelectorAll('.vs-filter-chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        const isActive = chip.classList.toggle('is-active');
        chip.setAttribute('aria-pressed', String(isActive));
        apply();
      });
    });

    clearButtons.forEach((button) => {
      button.addEventListener('click', () => {
        bar.querySelectorAll('.vs-filter-chip.is-active').forEach((chip) => {
          chip.classList.remove('is-active');
          chip.setAttribute('aria-pressed', 'false');
        });
        apply();
      });
    });

    apply();
  });
}

document.querySelectorAll('[data-header]').forEach(initHeader);
document.querySelectorAll('[data-editorial-carousel]').forEach(initEditorialCarousel);
initQuickAddForms();
initPolicyDialogs();
initVariantMedia();
initVariantPicker();
initCardSwatches();
initCollectionFilters();

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-header]').forEach(initHeader);
  event.target.querySelectorAll('[data-editorial-carousel]').forEach(initEditorialCarousel);
  initQuickAddForms(event.target);
  initPolicyDialogs(event.target);
  initVariantMedia(event.target);
  initVariantPicker(event.target);
  initCardSwatches(event.target);
  initCollectionFilters(event.target);
});
